import { ArdoqId, PersonalSetting, ViewIds } from '@ardoq/api-types';
import {
  setFocusedItem as setFocusedItemAction,
  setHoveredItem as setHoveredItemAction,
} from '@ardoq/dependency-map';
import type { UrlFieldValue } from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import { viewLegendCommands } from '@ardoq/view-legend';
import {
  updateViewContext as updateViewContextAction,
  type ViewContext,
} from '@ardoq/view-settings';
import { noop } from 'lodash';
import {
  openDetailsDrawer as openDetailsDrawerAction,
  updateDetailsDrawer as updateDetailsDrawerAction,
} from 'appLayout/ardoqStudio/detailsDrawer/actions';
import { componentSelectedInPresentationMode } from 'streams/components/ComponentActions';
import { updatePersonalSetting } from 'streams/currentUser/actions';
import { getLoadedPersonalSetting } from 'streams/currentUser/getLoadedPersonalSetting';
import { openSingleUrlFieldValue } from 'tabview/graphViews/openSingleUrlFieldValue';
import { navigateToComponent } from 'tabview/navigateToComponent';
import {
  recordRenderComplete,
  type ViewLoadMetrics,
} from 'tracking/durationTracking/viewPerformance';
import { componentSelectedInQuickExploreMode } from 'traverseOneLevel/actions';
import type { ViewpointModeDependencyMapSettingsControlsDependencies } from './viewpointMode/settingsControls';

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;

type DependencyMapBaseCommands = {
  updateDetailsDrawer: (componentId: ArdoqId) => void;
  selectComponent: (componentId: ArdoqId) => void;
  updateViewContext: (viewContext: ViewContext) => void;
  updateViewLegendSettings: (isActive: boolean) => void;
  openSingleUrlFieldValue: (
    urlFieldValues: UrlFieldValue[],
    element: HTMLElement
  ) => void;
  recordRenderComplete: (performanceTrackingId: string) => void;
  getNeverShowAgainUserSetting: () => Promise<boolean>;
  setNeverShowAgainUserSetting: (neverShowAgain: boolean) => void;
};

export type DependencyMapViewCommands = DependencyMapBaseCommands & {
  setHoveredItem: (componentId: ArdoqId | null, viewInstanceId: string) => void;
  setFocusedItem: (componentId: ArdoqId | null, viewInstanceId: string) => void;
};

const getNeverShowAgainUserSetting = async () =>
  (await getLoadedPersonalSetting(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_DEPENDENCY_MAP
  )) ?? false;

const setNeverShowAgainUserSetting = (neverShowAgain: boolean) =>
  dispatchAction(
    updatePersonalSetting({
      personalSetting:
        PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_DEPENDENCY_MAP,
      newValue: neverShowAgain,
    })
  );

const updateDetailsDrawer = (componentId: ArdoqId) =>
  dispatchAction(updateDetailsDrawerAction([componentId]));

const openDetailsDrawer = (componentId: ArdoqId) =>
  dispatchAction(openDetailsDrawerAction([componentId]));

const selectComponentInPresentationMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInPresentationMode({ id: componentId }));

const selectComponentInQuickExploreMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInQuickExploreMode(componentId));

const setHoveredItem = (componentId: ArdoqId | null, viewInstanceId: string) =>
  dispatchAction(setHoveredItemAction(componentId), viewInstanceId);

const setFocusedItem = (componentId: ArdoqId | null, viewInstanceId: string) =>
  dispatchAction(setFocusedItemAction(componentId), viewInstanceId);

const updateViewContext = (viewContext: ViewContext) =>
  dispatchAction(updateViewContextAction(viewContext));

const updateViewLegendSettings = (isActive: boolean) =>
  viewLegendCommands.updateViewLegendSettings({ viewId: VIEW_ID, isActive });

export type ViewpointModeDependencyMapViewCommandsConfig = {
  isPresentationMode: boolean;
  isQuickExploreSelectionEnabled: boolean;
};

const getSelectComponentInViewpointMode = ({
  isPresentationMode,
  isQuickExploreSelectionEnabled,
}: ViewpointModeDependencyMapViewCommandsConfig): ((
  componentId: ArdoqId
) => void) => {
  if (isPresentationMode) {
    return selectComponentInPresentationMode;
  }
  if (isQuickExploreSelectionEnabled) {
    return selectComponentInQuickExploreMode;
  }
  return openDetailsDrawer;
};

export const createViewpointModeDependencyMapViewCommands = (
  config: ViewpointModeDependencyMapViewCommandsConfig
): DependencyMapViewCommands => ({
  updateDetailsDrawer,
  selectComponent: getSelectComponentInViewpointMode(config),
  setHoveredItem,
  setFocusedItem,
  updateViewContext,
  updateViewLegendSettings,
  openSingleUrlFieldValue,
  recordRenderComplete,
  getNeverShowAgainUserSetting,
  setNeverShowAgainUserSetting,
});

export const WORKSPACE_MODE_DEPENDENCY_MAP_VIEW_COMMANDS: DependencyMapViewCommands =
  {
    updateDetailsDrawer: noop,
    selectComponent: navigateToComponent,
    setHoveredItem,
    setFocusedItem,
    updateViewContext,
    updateViewLegendSettings,
    openSingleUrlFieldValue,
    recordRenderComplete,
    getNeverShowAgainUserSetting,
    setNeverShowAgainUserSetting,
  };

export type ViewpointModeDependencyMapDataLayerCommands =
  DependencyMapBaseCommands & {
    onHoveredComponentChange: (componentId: ArdoqId | null) => void;
    onFocusedComponentChange: (componentId: ArdoqId | null) => void;
    recordViewModelReady: (
      performanceTrackingId: string,
      metrics: ViewLoadMetrics
    ) => void;
    updateViewProperties: (
      viewProperties: ViewpointModeDependencyMapSettingsControlsDependencies
    ) => void;
    updateViewEmptyStateData: (isViewEmpty: boolean) => void;
    updateViewComponentIds: (componentIds: ArdoqId[]) => void;
  };

export const NOOP_VIEWPOINT_MODE_DEPENDENCY_MAP_DATA_LAYER_COMMANDS: ViewpointModeDependencyMapDataLayerCommands =
  {
    updateDetailsDrawer: noop,
    selectComponent: noop,
    onHoveredComponentChange: noop,
    onFocusedComponentChange: noop,
    updateViewContext: noop,
    updateViewLegendSettings: noop,
    openSingleUrlFieldValue: noop,
    recordRenderComplete: noop,
    recordViewModelReady: noop,
    getNeverShowAgainUserSetting: () => Promise.resolve(false),
    setNeverShowAgainUserSetting: noop,
    updateViewProperties: noop,
    updateViewEmptyStateData: noop,
    updateViewComponentIds: noop,
  };

export const EMBEDDED_DEPENDENCY_MAP_VIEW_COMMANDS: DependencyMapViewCommands =
  {
    updateDetailsDrawer: noop,
    selectComponent: noop,
    setHoveredItem,
    setFocusedItem,
    updateViewContext,
    updateViewLegendSettings,
    openSingleUrlFieldValue,
    recordRenderComplete,
    getNeverShowAgainUserSetting,
    setNeverShowAgainUserSetting,
  };
